import { useState, type DragEvent } from 'react';
import { ActionIcon, Group, Menu, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconGripVertical, IconTrash, IconFileSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import type { RunsheetEntry } from '../../types/api';
import { useDeleteEntry, useReorderEntry } from '../../queries/runsheet-entries';
import { useDocumentTypes } from '../../queries/document-types';
import { InlineCellText } from './inline-cells/InlineCellText';
import { InlineCellDate } from './inline-cells/InlineCellDate';
import { InlineCellDocType } from './inline-cells/InlineCellDocType';

interface Props {
  entries: RunsheetEntry[];
  loading: boolean;
  canWrite: boolean;
  selectedId: string | null;
  onSelect: (e: RunsheetEntry) => void;
  onOpenDrawer: (e: RunsheetEntry) => void;
}

export function EntryGrid({
  entries,
  loading,
  canWrite,
  selectedId,
  onSelect,
  onOpenDrawer,
}: Props) {
  const { data: docTypes = [] } = useDocumentTypes();
  const deleteM = useDeleteEntry();
  const reorderM = useReorderEntry();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  function onDragStart(id: string, e: DragEvent<HTMLElement>) {
    if (!canWrite) {
      e.preventDefault();
      return;
    }
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }

  function onDragOver(id: string, e: DragEvent<HTMLElement>) {
    if (!draggingId || draggingId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(id);
  }

  function onDrop(targetId: string, e: DragEvent<HTMLElement>) {
    e.preventDefault();
    const src = draggingId ?? e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDropTargetId(null);
    if (!src || src === targetId) return;
    // Place dragged row after targetId (visually positioned below targetId).
    reorderM.mutate({ id: src, after_id: targetId });
  }

  function dropAtTop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    const src = draggingId ?? e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDropTargetId(null);
    if (!src) return;
    reorderM.mutate({ id: src, after_id: null });
  }

  function confirmDelete(entry: RunsheetEntry) {
    modals.openConfirmModal({
      title: 'Delete entry?',
      children: (
        <Text size="sm">
          Soft-deletes this runsheet entry. You can still see it in the audit log.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteM.mutate(entry.id),
    });
  }

  return (
    <>
      {/* Invisible drop zone above the table for "move to top". */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={dropAtTop}
        style={{
          height: draggingId ? 12 : 0,
          background:
            draggingId && dropTargetId === null ? 'var(--mantine-color-pine-1)' : 'transparent',
          transition: 'height 0.12s ease',
          borderRadius: 4,
        }}
      />

      <DataTable<RunsheetEntry>
        className="rt-entry-grid"
        records={entries}
        fetching={loading}
        minHeight={240}
        highlightOnHover
        noRecordsText="No entries yet - click Add Entry to start"
        rowClassName={(r) => (r.id === selectedId ? 'mantine-datatable-row-selected' : '')}
        onRowClick={({ record, event }) => {
          // clicks inside inline editors / action menus shouldn't select the row
          const target = event.target as HTMLElement;
          if (target.closest('[data-inline-editor], [data-row-actions]')) return;
          onSelect(record);
        }}
        columns={[
          {
            accessor: 'drag',
            title: '',
            width: 32,
            render: (r) =>
              canWrite ? (
                <span
                  draggable
                  className="rt-drag-handle"
                  onDragStart={(e) => onDragStart(r.id, e)}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDropTargetId(null);
                  }}
                  onDragOver={(e) => onDragOver(r.id, e)}
                  onDrop={(e) => onDrop(r.id, e)}
                  title="Drag to reorder"
                  style={{ display: 'inline-flex', padding: 2 }}
                >
                  <IconGripVertical size={16} />
                </span>
              ) : null,
          },
          {
            accessor: 'index',
            title: '#',
            width: 40,
            render: (_r, idx) => (
              <Text size="xs" c="dimmed">
                {idx + 1}
              </Text>
            ),
          },
          {
            accessor: 'instrument_date',
            title: 'Instrument',
            width: 130,
            render: (r) =>
              canWrite ? (
                <InlineCellDate entry={r} field="instrument_date" />
              ) : (
                (r.instrument_date ?? '—')
              ),
          },
          {
            accessor: 'recording_date',
            title: 'Recorded',
            width: 130,
            render: (r) =>
              canWrite ? (
                <InlineCellDate entry={r} field="recording_date" />
              ) : (
                (r.recording_date ?? '—')
              ),
          },
          {
            accessor: 'document_type_id',
            title: 'Type',
            width: 150,
            render: (r) =>
              canWrite ? (
                <InlineCellDocType entry={r} />
              ) : (
                (docTypes.find((t) => t.id === r.document_type_id)?.code ?? '—')
              ),
          },
          {
            accessor: 'book_page',
            title: 'Book/Page',
            width: 130,
            render: (r) =>
              canWrite ? (
                <Group gap={4} wrap="nowrap">
                  <InlineCellText entry={r} field="book" placeholder="Bk" width={56} />
                  <Text size="xs" c="dimmed">
                    /
                  </Text>
                  <InlineCellText entry={r} field="page" placeholder="Pg" width={56} />
                </Group>
              ) : (
                `${r.book ?? '—'} / ${r.page ?? '—'}`
              ),
          },
          {
            accessor: 'instrument_number',
            title: 'Inst #',
            width: 140,
            render: (r) =>
              canWrite ? (
                <InlineCellText entry={r} field="instrument_number" />
              ) : (
                (r.instrument_number ?? '—')
              ),
          },
          {
            accessor: 'remarks',
            title: 'Remarks',
            ellipsis: true,
            render: (r) =>
              r.remarks ? (
                <Text size="sm" lineClamp={1}>
                  {r.remarks}
                </Text>
              ) : (
                <Text size="sm" lineClamp={1} c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'updated_at',
            title: 'Updated',
            width: 90,
            render: (r) => (
              <Text size="xs" c="dimmed">
                {dayjs(r.updated_at).format('MM-DD HH:mm')}
              </Text>
            ),
          },
          {
            accessor: 'actions',
            title: '',
            width: 48,
            render: (r) => (
              <span data-row-actions>
                <Group gap={2} justify="flex-end" wrap="nowrap">
                  <Tooltip label="Open full form">
                    <ActionIcon size="sm" variant="subtle" onClick={() => onOpenDrawer(r)}>
                      <IconFileSearch size={14} />
                    </ActionIcon>
                  </Tooltip>
                  {canWrite && (
                    <Menu withArrow position="bottom-end">
                      <Menu.Target>
                        <ActionIcon size="sm" variant="subtle">
                          <IconDotsVertical size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => confirmDelete(r)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </span>
            ),
          },
        ]}
        rowStyle={(r) =>
          r.id === dropTargetId
            ? { boxShadow: 'inset 0 -2px 0 var(--mantine-color-pine-6)' }
            : r.id === draggingId
              ? { opacity: 0.5 }
              : {}
        }
      />
    </>
  );
}
